import { useState, useCallback, useMemo } from "react";

import { APIError } from "../../../../app/modules/auth";
import {
    createFCACustomFolderAndFields,
    FullCircleOrganization,
    updateFCAOrganization
} from "../../../core/_requests";
import { useFullCircleOrganizationDetail } from "./FCAManageOrgDetail";
import useAccounts from "../../../hooks/useAccounts";
// import { useServiceLocationDetail } from "../../service-locations/detail/ServiceLocationDetail";
import { DisableLinkButton, GetAvailableLocationsGivenAServiceLocation } from "../../service-locations/detail/ServiceLocationSettings";


function CreateFCACustomFolderAndFields({
    linkOutToFRMWithURL,
    fcaOrganization
}: {
    linkOutToFRMWithURL: string | null,
    fcaOrganization: FullCircleOrganization
}) {
    const [creating, setCreating] = useState(false)
    const [errorCreating, setErrorCreating] = useState<string | null>(null)

    const handleCreateOnlineArrangementFolder = useCallback(async () => {
        try {
            setCreating(true)
            setErrorCreating(null)
            await createFCACustomFolderAndFields({
                organization_unique_id: fcaOrganization.unique_identifier
            })
            window.location.reload()
        } catch (exception: any) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to create custom folder and fields for FCA organization'
            setErrorCreating(errorMessage)
        } finally {
            setCreating(false)
        }
    }, [fcaOrganization.unique_identifier])

    return <div className='mt-5'>
        <div className='separator separator-dashed my-5'></div>
        <div>
            <h2>
                FCA Custom Folder and Fields in 360
            </h2>
        </div>
        {fcaOrganization.private_data && <>
            {!fcaOrganization.private_data.custom_folder_and_fields_have_been_created ? <div>
                {errorCreating && <div className='mt-5'>
                    <div className="alert alert-danger" role="alert">
                        {errorCreating}
                    </div>
                </div>}
                <button className='btn btn-primary btn-lg' onClick={() => handleCreateOnlineArrangementFolder()} disabled={creating}>
                    Create FCA Folder and Fields
                </button>
            </div> : <div>
                <h4>
                    FCA Folder and Fields Created
                </h4>
                <div>
                    <a href={`${linkOutToFRMWithURL}/v2/location/${fcaOrganization.private_data.linked_to_frm_location_id}/settings/fields`} target='_blank' rel='noreferrer'>
                        View in FRM
                    </a>
                </div>
            </div>}
        </>}
    </div>
}


function FCAOrgSettings360Details() {
    const {
        fcaOrganization
    } = useFullCircleOrganizationDetail();

    const { accounts } = useAccounts() // redundant retrieval but it'll make sure it's updated. May be in a global context one day.

    const [viewingAvailableLocations, setViewingAvailableLocations] = useState(false)

    const hardcodedPremierAccountUniqueId = "acct_GYtjmDNf2raFHTBX"; // this is hardcoded for now, see note below in the linkOutToFRMWithURL

    const linkOutToFRMWithURL = useMemo(() => {
        //
        // Right now service locations don't have GHL details because it's an admin detail not added to that
        // retrieval. The quickest fix right now is looking up the admin account mapping
        // and deducing the URL from that.
        //
        if (!fcaOrganization) {
            return null
        }
        // hardcoding acct_GYtjmDNf2raFHTBX because we are going to assume only Premier for a while,
        // which should save us hours in needing to make this work better
        const matchingAccounts = accounts.filter(x => x.unique_identifier === hardcodedPremierAccountUniqueId);
        if (!matchingAccounts.length) {
            return null
        }
        const account = matchingAccounts[0];
        if (account.external_ghl_site_url) {
            return account.external_ghl_site_url
        }
        return null
    }, [accounts, fcaOrganization])

    return <div>
        {viewingAvailableLocations ? <div>
            <div className='mt-5'>
                <GetAvailableLocationsGivenAServiceLocation
                    serviceLocationUniqueId={fcaOrganization.unique_identifier}
                    accountUniqueId={hardcodedPremierAccountUniqueId} // this is hardcoded for now, see note above
                />
            </div>
            <div className='mt-5'>
                <button className='btn btn-secondary btn-lg' onClick={() => setViewingAvailableLocations(false)}>
                    Back
                </button>
            </div>
        </div> : <div className='mt-5 mb-10'>
            {!fcaOrganization.private_data?.linked_to_frm_location_id ? <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={() => setViewingAvailableLocations(true)}>
                    Link to FRM
                </button>
            </div> : <div>
                <div className='mt-5'>
                    Linked to FRM
                </div>
                {!linkOutToFRMWithURL ? <div>
                    FRM Location ID: {fcaOrganization.private_data.linked_to_frm_location_id}
                </div> : <div>
                    {/* /v2/location/IQzMIO91nK1KTq0PCwq8/contacts/smart_list/All */}
                    FRM Location ID: <a href={`${linkOutToFRMWithURL}/v2/location/${fcaOrganization.private_data.linked_to_frm_location_id}/contacts/smart_list/All`} target='_blank' rel='noreferrer'>
                        {fcaOrganization.private_data?.linked_to_frm_location_id}
                    </a>
                </div>}
                <div className='mt-5'>
                    <DisableLinkButton
                        setViewingAvailableLocations={setViewingAvailableLocations}
                        serviceLocationUniqueId={fcaOrganization.unique_identifier}
                        onSuccessfullyDisabled={() => {
                            window.location.reload()
                        }}
                        accountUniqueId={hardcodedPremierAccountUniqueId} // this is hardcoded for now, see note above
                    />
                </div>
                <CreateFCACustomFolderAndFields
                    linkOutToFRMWithURL={linkOutToFRMWithURL}
                    fcaOrganization={
                        fcaOrganization
                    }
                />
            </div>}
        </div>}
    </div>
}

function FCAOrgSettingsDetails() {
    const {
        fcaOrganization
    } = useFullCircleOrganizationDetail();

    const [savingServiceLocation, setSavingServiceLocation] = useState(false)

    const [errorUpdating, setErrorUpdating] = useState<string | null>(null)

    const [ignoreAsNeedingAttention, setIgnoreAsNeedingAttention] = useState(fcaOrganization.private_data?.ignore_as_needing_attention || false)

    const handleUpdateServiceLocation = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setSavingServiceLocation(true)
        setErrorUpdating(null)
        try {
            await updateFCAOrganization({
                organization_unique_id: fcaOrganization.unique_identifier,
                private_data: {
                    ...fcaOrganization.private_data,
                    ignore_as_needing_attention: ignoreAsNeedingAttention
                }
            })
            // until we have a global context, reload the page.
            window.location.reload()
        } catch (exception: any) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to update FCA organization'
            setErrorUpdating(errorMessage)
            setSavingServiceLocation(false)
        } finally {
            // We don't set setSavingServiceLocation(false) here because we're reloading the page.
        }
    }, [fcaOrganization.unique_identifier, fcaOrganization.private_data, ignoreAsNeedingAttention])

    return <div>
        <form className='mt-5' onSubmit={handleUpdateServiceLocation}>
            <div className='mb-5'>
                <label className='form-label'>Organization Name</label>
                <input
                    className='form-control'
                    type='text'
                    value={fcaOrganization.org_name || ''}
                    disabled
                />
                <small className='form-text text-muted mt-2'>
                    This Organization Name is set in Pipedrive and for now cannot be changed (for now)
                </small>
            </div>
            <div className='mb-5'></div>
            <div className='form-check mb-5'>
                <input
                    className='form-check-input'
                    type='checkbox'
                    id='ignoreOnlineArrangements'
                    checked={ignoreAsNeedingAttention}
                    onChange={(event) => setIgnoreAsNeedingAttention(event.target.checked)}
                />
                <label className='form-check-label' htmlFor='ignoreOnlineArrangements'>
                    Ignore as "Needing Attention"
                </label>
            </div>
            {/* submit: */}
            <div>
                <button className='btn btn-primary btn-sm' type='submit' disabled={savingServiceLocation}>
                    {savingServiceLocation ? <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Loading...</span>
                    </div> : 'Save'}
                </button>
            </div>
            {errorUpdating && <div className='mt-5'>
                <div className="alert alert-danger" role="alert">
                    {errorUpdating}
                </div>
            </div>}
        </form>
    </div>
}


function FCAOrgSettings() {
    const [viewingSubTab, setViewingSubTab] = useState<'details' | '360'>('360')

    return <div>
        <div className="header-menu align-items-stretch mb-5">
            <div className="menu menu-lg-rounded menu-column menu-lg-row menu-state-bg menu-title-gray-700 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-400 fw-bold my-5 my-lg-0 align-items-stretch">
                <div className="menu-item me-lg-1">
                    <button className={["menu-link py-3 btn btn-link-primary", viewingSubTab === 'details' ? "active" : ""].join(" ")}
                        onClick={() => setViewingSubTab('details')}
                        type="button"
                    >
                        <span className="menu-title">Details</span>
                    </button>
                </div>
                <div className="menu-item me-lg-1">
                    <button className={["menu-link py-3 btn btn-link-primary", viewingSubTab === '360' ? "active" : ""].join(" ")}
                        onClick={() => setViewingSubTab('360')}
                        type="button"
                    >
                        <span className="menu-title">360 integration</span>
                    </button>
                </div>
            </div>
        </div>
        <div>
            {viewingSubTab === 'details' && <FCAOrgSettingsDetails />}
            {viewingSubTab === '360' && <FCAOrgSettings360Details />}
        </div>
    </div>
}

export default FCAOrgSettings;